import { type FC, useState, useCallback } from "react";
import { HomeIcon, GlobeIcon, FileTextIcon } from "lucide-react";
import { useTabs } from "@/contexts/tabsContext";
import type { TabData } from "rc-dock";
import { parseTabId } from "@/contexts/tabsContext";
import { TabTitle } from "@/chat2/components/chatlayout/TabTitle";
import { useEventListeners } from "@/hooks/useEventListeners";
import { WikiFormContext, type WikiFormEvents } from "./wikiFormContext";
import Dynamic from "@/lib/dynamic";
import ComponentSpinner from "@/components/ui/componentSpinner";

// Lazy load heavy wiki components (pulls in Milkdown/Crepe/ProseMirror/CodeMirror)
const WikiScopeTab = Dynamic(() => import("./WikiScopeTab").then(mod => ({ default: mod.WikiScopeTab })), { loading: () => <ComponentSpinner /> });
const WikiPageEditActions = Dynamic(() => import("./WikiScopeTab").then(mod => ({ default: mod.WikiPageEditActions })), { loading: () => <ComponentSpinner /> });
const WikiPageContent = Dynamic(() => import("@/components/wiki/WikiPageContent").then(mod => ({ default: mod.WikiPageContent })), { loading: () => <ComponentSpinner /> });

// ============================================================================
// Wiki Scope Tab Renderer
// ============================================================================

export const WikiScopeTabRenderer = (id: string): TabData => {
  const { entityId } = parseTabId(id);
  const scope = (entityId ?? "personal") as "personal" | "system";
  return {
    id,
    title: (
      <div className="flex items-center gap-1.5 whitespace-nowrap">
        {scope === "system" ? (
          <GlobeIcon className="w-3.5 h-3.5" />
        ) : (
          <HomeIcon className="w-3.5 h-3.5" />
        )}
        {scope === "system" ? "System Wiki" : "Personal Wiki"}
      </div>
    ),
    content: <WikiScopeTab scope={scope} tabId={id} />,
  };
};

// ============================================================================
// Wiki Page Tab Renderer
// ============================================================================

export const WikiPageTabRenderer = (id: string): TabData => {
  const { entityId } = parseTabId(id);
  const pageId = entityId ?? "";
  return {
    id,
    title: <TabTitle id={id} />,
    content: <WikiPageTab pageId={pageId} />,
  };
};

// ============================================================================
// WikiPageTab
// ============================================================================

const WikiPageTab: FC<{ pageId: string }> = ({ pageId }) => {
  const formEvents = useEventListeners<WikiFormEvents>("wiki-page-form");
  const tabs = useTabs(false);
  const [editing, setEditing] = useState(false);

  const handleDeleted = useCallback(() => {
    tabs?.close(`wiki-page:${pageId}`);
  }, [tabs, pageId]);

  return (
    <WikiFormContext.Provider value={formEvents}>
      <div className="flex flex-col h-full">
        {editing && (
          <div className="flex items-center justify-end gap-2 px-3 py-1.5 border-b border-gray-200 dark:border-gray-700 shrink-0">
            <WikiPageEditActions pageEvents={formEvents} editing={editing} />
          </div>
        )}
        <div className="flex-1 min-h-0 overflow-auto">
          <WikiPageContent pageId={pageId} tabId={`wiki-page:${pageId}`} onEditingChange={setEditing} onDeleted={handleDeleted} />
        </div>
      </div>
    </WikiFormContext.Provider>
  );
};
